import { useQuery } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { queryKeys } from './queryKeys';

export type MetricName =
  | 'cpu'
  | 'memory'
  | 'filesystem'
  | 'network_rx'
  | 'network_tx'
  | 'heartbeat';

export interface MetricSeriesPoint {
  time: string;
  value: number;
}

export interface ResourceMetricsParams {
  namespace?: string;
  /** One series per namespace. Mutually exclusive with `namespace`. */
  groupByNamespace?: boolean;
  kind?: string;
  name?: string;
  metric: MetricName;
  aggregation: string;
  from: number;
  to: number;
}

export function useResourceMetrics(
  environmentId: EnvironmentId,
  params: ResourceMetricsParams,
  {
    enabled = true,
    onError,
  }: { enabled?: boolean; onError?: (error: unknown) => void } = {}
) {
  return useQuery(
    queryKeys.observabilityMetrics(environmentId, params),
    () => getResourceMetrics(environmentId, params),
    // No withError: each card renders its own "Failed to load" placeholder
    // (see MetricCard) and reports through onError instead, so callers can
    // dedupe the notification across the several cards sharing one range.
    // retry: 1 — a rejected range (e.g. over 31 days) fails the same way
    // every time, so the default 3 retries just delay the error.
    { enabled, onError, retry: 1 }
  );
}

async function getResourceMetrics(
  environmentId: EnvironmentId,
  {
    namespace,
    groupByNamespace,
    kind,
    name,
    metric,
    aggregation,
    from,
    to,
  }: ResourceMetricsParams
): Promise<MetricSeriesPoint[]> {
  const qs = new URLSearchParams();
  if (namespace) qs.set('namespace', namespace);
  if (groupByNamespace) qs.set('groupBy', 'namespace');
  if (kind && name) {
    qs.set('kind', kind);
    qs.set('name', name);
  }
  qs.set('metric', metric);
  qs.set('aggregation', aggregation);
  qs.set('from', new Date(from * 1000).toISOString());
  qs.set('to', new Date(to * 1000).toISOString());

  try {
    const { data } = await axios.get<{ data: Record<string, unknown>[] }>(
      `/observability/environments/${environmentId}/metrics?${qs.toString()}`
    );
    return normalizeMetricSeries(data.data);
  } catch (error) {
    throw parseAxiosError(error);
  }
}

// normalizeMetricSeries reads OneUptime's AggregatedModel shape — each row
// carries `timestamp` and `value` (see AnalyticsDatabaseService.aggregateBy
// upstream) — and tolerates rows missing a usable pair instead of throwing.
function normalizeMetricSeries(
  rows: Record<string, unknown>[] | undefined
): MetricSeriesPoint[] {
  if (!rows) {
    return [];
  }

  return rows.flatMap((row) => {
    const time = row.timestamp;
    const value = row.value;
    if (typeof time !== 'string' || typeof value !== 'number') {
      return [];
    }
    return [{ time, value }];
  });
}
